<script setup lang="ts">
import { useSeo, VImg } from '@hatfieldmedia/hmcore'
import { ref, watch } from 'vue'
import { useScrollReveal } from '@/composables/useScrollReveal'
import ContactFormModal from '@/components/ContactFormModal.vue'
import EventCard from '@/components/EventCard.vue'
import EventDetailModal from '@/components/EventDetailModal.vue'
import LearnMoreModal from '@/components/LearnMoreModal.vue'
import RegistrationModal from '@/components/RegistrationModal.vue'
import BenefitsAccordion from '@/components/BenefitsAccordion.vue'
import SectionHeader from '@/components/SectionHeader.vue'
import ServiceCard from '@/components/ServiceCard.vue'
import SiteFooterCustom from '@/components/SiteFooterCustom.vue'
import TestimonialCard from '@/components/TestimonialCard.vue'
import SiteNav from '@/components/SiteNav.vue'
import BaseButton from '@/components/BaseButton.vue'
import type { ServiceData } from '@/components/LearnMoreModal.vue'
import type { Event, EventStatus } from '@/types/event'

useSeo({
  title: 'M6 Elite Combine Testing',
  description:
    'Objective performance testing built to give athletes clarity, confidence, and a measurable path forward.',
  image: '/media/images/mpower6-elite-banner-image-cropped.jpg',
  pageType: 'WebPage',
})

useScrollReveal()

// --- Modal state ---
const showRegistration = ref(false)
const showContact = ref(false)
const showLearnMore = ref(false)
const selectedService = ref<ServiceData | null>(null)
const contactInterest = ref('')
const selectedEventSlug = ref('')
const showEventDetail = ref(false)
const selectedEventDetailType = ref<'football' | 'basketball' | null>(null)
const selectedEventStatus = ref<EventStatus>('open')

function openRegistration(slug: string) {
  selectedEventSlug.value = slug
  showRegistration.value = true
}

function openEventDetail(slug: string) {
  const ev = events.find((e) => e.slug === slug)
  if (!ev) return
  selectedEventDetailType.value = ev.detailType as 'football' | 'basketball'
  selectedEventStatus.value = ev.status
  showEventDetail.value = true
}

function openRegistrationFromEventDetail() {
  showEventDetail.value = false
  showRegistration.value = true
}

function openContactFromEventDetail() {
  showEventDetail.value = false
  showContact.value = true
}

watch(showRegistration, (open) => {
  if (!open) {
    selectedEventSlug.value = ''
  }
})

// --- Events data ---
const events: Event[] = [
  {
    slug: 'summer-2026',
    image: '/media/images/extracted/image-02.jpg',
    date: '2026-06-27',
    title: 'M6 Elite Summer Combine<br>(Football)',
    description: 'Ages 14+ | Co-ed | 100 spots available | 10 a.m.–1 p.m.',
    location: 'King Louies Outdoor Fields — Louisville, KY',
    detailType: 'football',
    status: 'cancelled' as const,
  },
  {
    slug: 'shoot360-youth',
    image: '/media/images/extracted/image-03.jpg',
    date: '2026-07-25',
    title: 'M6 Elite Combine x Shoot 360<br>(Basketball)',
    description: 'Ages 10–13 | Co-ed | 50 spots available | 10 a.m.–1 p.m.',
    location: 'Shoot 360 — 9400 Dayflower St, Prospect, KY 40059',
    detailType: 'basketball',
    status: 'open' as const,
  },
  {
    slug: 'shoot360-14plus',
    image: '/media/images/extracted/image-04.jpg',
    date: '2026-08-01',
    title: 'M6 Elite Combine x Shoot 360<br>(Basketball)',
    description: 'Ages 14+ | Co-ed | 50 spots available | 10 a.m.–1 p.m.',
    location: 'Shoot 360 — 9400 Dayflower St, Prospect, KY 40059',
    detailType: 'basketball',
    status: 'open' as const,
  },
].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

// --- Services data ---
const services = [
  {
    image: '/media/images/service-1on1.jpg',
    title: '1-on-1 Testing Evaluations',
    description:
      'Personalized assessments using laser-timed sprints, VALD ForceDecks, vertical/broad jump data, and individual performance reporting.',
    serviceType: '1on1',
  },
  {
    image: '/media/images/service-team.jpg',
    title: 'Team Evaluations',
    description:
      'Roster-wide testing with standardized protocols, team and athlete reports, and baseline data for readiness, fatigue, and development tracking.',
    serviceType: 'team',
  },
  {
    image: '/media/images/service-combine.jpg',
    title: 'Combine Events',
    description:
      'Large-scale testing events with VALD ForceDecks, laser timing, vertical/broad jump stations, and real-time leaderboards.',
    serviceType: 'combine',
  },
]

// --- Service detail data for learn-more modal ---
const serviceDetails: Record<string, ServiceData> = {
  '1on1': {
    serviceType: '1on1',
    badge: '1-on-1 Service',
    title: '1-on-1 Testing Evaluations',
    description:
      'Get a complete picture of your athletic profile with a private, one-on-one testing session. Our certified performance specialists guide you through every assessment, breaking down your results in real time so you leave with actionable insights — not just numbers.',
    features: [
      'Laser-timed 10, 40, and 60-yard sprints with split analysis',
      'VALD ForceDecks force plate testing for jump power and asymmetry detection',
      'Vertical jump and standing broad jump measurement',
      'Pro-Agility (5-10-5), Lane Agility, L-Drill, and T-Test with laser timing',
      'BlazePod reactive agility and cognitive processing assessment',
      'Comprehensive individual performance report with percentile rankings',
      'Personalized training recommendations based on your data',
      'Results delivered within 48 hours via email',
    ],
    closingText:
      'Perfect for athletes who want detailed, individualized attention and a deep dive into their performance data.',
    image: '/media/images/service-1on1.jpg',
  },
  team: {
    serviceType: 'team',
    badge: 'Team Service',
    title: 'Team Evaluations',
    description:
      'Bring your entire roster through a standardized testing protocol designed to give coaches objective data on every athlete. Identify strengths, flag injury risk through asymmetry screening, and establish baseline metrics for the season — all in one session.',
    features: [
      'Standardized testing protocols for consistent team-wide comparison',
      'Individual athlete reports plus aggregated team analytics dashboard',
      'Position-specific benchmarking and percentile rankings',
      'Asymmetry and bilateral deficit screening for injury prevention',
      'Pre-season, mid-season, and post-season testing packages available',
      'Fatigue monitoring and readiness baseline data',
      'Exportable data for coaching staff in spreadsheet and PDF formats',
      'On-site testing available at your facility or at MPower6 HQ',
    ],
    closingText:
      'Trusted by high school and club programs across the region. Minimum 10 athletes per team session.',
    image: '/media/images/service-team.jpg',
  },
  combine: {
    serviceType: 'combine',
    badge: 'Event',
    title: 'Combine Events',
    description:
      'The flagship M6 experience. Large-scale combine events bring together top athletes from across the country for a pro-level testing day. Compete head-to-head, see where you rank on the live leaderboard, and get your verified results in front of coaches and scouts.',
    features: [
      'Full pro-style testing battery for every sport in one event',
      'VALD ForceDecks, laser timing gates, and vertical jump stations',
      'Real-time digital leaderboard displayed throughout the event',
      'Professional event photography and video highlights',
      'Verified results shared with our college coaching network',
      'Post-event individual performance report with national percentiles',
      'Award recognition for top performers at each station',
      'Multiple age groups and divisions for fair competition',
    ],
    closingText:
      'Limited spots per event. Register early to secure your place and compete against the best.',
    image: '/media/images/service-combine.jpg',
  },
}

// --- Benefits data ---
const benefits = [
  {
    title: 'Pro-Level Testing Technology',
    content:
      'VALD ForceDecks, laser-timed sprints, and vertical/broad jump measurement — the same tools used by NFL, NBA, and NCAA programs.',
  },
  {
    title: 'Individualized Performance Reports',
    content:
      'Every athlete receives a detailed performance profile with metrics, percentile rankings, and actionable development insights.',
  },
  {
    title: 'Exposure & Recruiting Visibility',
    content:
      'Results shared with our network of college coaches and scouts. Get noticed by programs actively looking for verified athletic data.',
  },
  {
    title: 'Multi-Sport Athletes Welcome',
    content:
      'Our testing protocols are sport-agnostic — football, basketball, soccer, baseball, track — every athlete benefits from knowing their numbers.',
  },
  {
    title: 'Trusted by Top Programs',
    content:
      'Teams, academies, and trainers across the country trust M6 data to benchmark, track, and develop their athletes year-round.',
  },
]

// --- Testimonials data ---
const testimonials = [
  {
    quote:
      'The M6 Combine gave me real numbers I could send to coaches. Two weeks later I had three schools reach out. This changed everything for me.',
    author: 'Marcus J.',
    role: 'Class of 2026',
  },
  {
    quote:
      "We brought our entire roster. The team reports helped us identify strengths and weaknesses we didn't even know existed. Game-changer for our program.",
    author: 'Coach Davis',
    role: 'Louisville HS',
  },
  {
    quote:
      'Professional setup, incredible staff, and the data speaks for itself. My son improved his 40 time by 0.3 seconds after following the recommendations.',
    author: 'Sarah T.',
    role: 'Parent',
  },
]

// --- Handlers ---
function openLearnMore(serviceType: string) {
  selectedService.value = serviceDetails[serviceType] ?? null
  showLearnMore.value = true
}

function openContactFromLearnMore() {
  showLearnMore.value = false
  contactInterest.value = selectedService.value?.title ?? ''
  showContact.value = true
}

function openRegistrationFromLearnMore() {
  showLearnMore.value = false
  showRegistration.value = true
}
</script>

<template>
  <!-- Page-wide decorative SVG background -->
  <svg
    class="page-bg-svg"
    viewBox="0 0 1440 900"
    preserveAspectRatio="xMidYMid slice"
    fill="none"
    stroke="#C8D0DC"
    stroke-linecap="round"
    aria-hidden="true"
  >
    <path
      d="M95 80c38 3 64 28 61 60s-30 58-66 55-62-29-59-60S58 77 95 80z"
      stroke-width="8"
      fill="none"
    ></path>
    <path d="M255 45l52 58M309 47l-50 54" stroke-width="8"></path>
    <path
      d="M1150 320c42 3 70 32 67 65s-33 62-72 59-66-32-63-65S1110 317 1150 320z"
      stroke-width="8.5"
      fill="none"
    ></path>
    <path d="M120 620l56 62M178 622l-54 58" stroke-width="8"></path>
    <path
      d="M290 680c22 2 38 17 36 35s-18 33-38 31-36-17-34-35S270 678 290 680z"
      stroke-width="7.5"
      fill="none"
    ></path>
    <path
      d="M1080 700c34 2 58 24 55 52s-27 50-58 48-54-25-52-52S1048 698 1080 700z"
      stroke-width="8"
      fill="none"
    ></path>
    <path d="M1260 640l48 52M1310 642l-46 50" stroke-width="7.5"></path>
    <path d="M60 430l40 44M102 432l-38 40" stroke-width="7"></path>
  </svg>

  <!-- Navigation -->
  <SiteNav @register="showRegistration = true" />

  <main>
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg"></div>
      <div class="hero-overlay"></div>
      <div class="hero-content reveal">
        <div class="hero-eyebrow">Performance Testing | Louisville, KY</div>
        <h1 class="hero-headline">
          Built for Athletes Who Compete With<br />
          <span>Purpose</span>
        </h1>
        <p class="hero-sub">
          Objective performance testing built to give athletes
          <em>clarity, confidence,</em> and a measurable path forward.
        </p>
        <div class="hero-btns">
          <BaseButton @click="showRegistration = true">Register Now</BaseButton>
          <BaseButton variant="outline">
            <a href="#our-method">Learn More</a>
          </BaseButton>
        </div>
      </div>
    </section>

    <!-- Video Section -->
    <section id="our-method" class="video-section section-pad text-center">
      <SectionHeader
        label="The M6 Method"
        title="Designed for<br>Tomorrow's Athlete"
        centered
        class="reveal"
      />
      <p class="video-body reveal">
        Pro-level testing protocols, VALD ForceDecks, laser timing, and real-time leaderboards —
        every metric verified, every result actionable.
      </p>
      <div class="video-wrap reveal">
        <div class="iframe-wrapper">
          <iframe
            src="https://www.youtube.com/embed/m6sP_Dg6TrA?si=C1ElSKOMrNePK5of&autoplay=1&mute=1"
            title="YouTube video player"
            frameborder="0"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
              web-share;
            "
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    </section>

    <div class="gradient-bar-fade"></div>

    <!-- Events Section -->
    <section id="events" class="section-pad text-center">
      <SectionHeader label="Upcoming" title="Combine Events" centered class="reveal" />
      <div class="events-grid reveal">
        <EventCard
          v-for="event in events"
          :key="event.title"
          :image="event.image"
          :date="event.date"
          :title="event.title"
          :description="event.description"
          :location="event.location"
          :slug="event.slug"
          :detail-type="event.detailType"
          :status="event.status"
          @register="openRegistration"
          @learn-more="openEventDetail"
        />
      </div>
    </section>

    <div class="gradient-bar-fade"></div>

    <!-- Services Section -->
    <section id="services" class="services-section section-pad text-center">
      <SectionHeader label="What We Offer" title="Our Testing Services" centered class="reveal" />
      <div class="services-grid reveal">
        <ServiceCard
          v-for="svc in services"
          :key="svc.serviceType"
          :image="svc.image"
          :title="svc.title"
          :description="svc.description"
          :service-type="svc.serviceType"
          @learn-more="openLearnMore"
        />
      </div>
    </section>

    <div class="gradient-bar-fade"></div>

    <!-- CTA Section -->
    <section class="cta-section section-pad">
      <div class="cta-inner reveal">
        <div class="cta-text">
          <p class="section-label">Don't Wait</p>
          <h2>Your Next Level <span>Starts Here</span></h2>
          <p>
            Spots are limited for each combine event. Secure your place and get the data, exposure,
            and edge you need to compete at the highest level.
          </p>
          <div class="cta-btns">
            <BaseButton @click="showRegistration = true">Register Now</BaseButton>
          </div>
        </div>
        <VImg
          src="/media/images/MP_AthletesRunning.png"
          class="cta-cutout"
          :width="380"
          :height="420"
          fit="fill"
        />
      </div>
    </section>

    <div class="gradient-bar-fade"></div>

    <!-- Benefits Section -->
    <section id="benefits" class="benefits-section section-pad">
      <div class="text-center">
        <SectionHeader label="Why M6" title="Benefits Breakdown" centered class="reveal" />
      </div>
      <div class="benefits-layout reveal">
        <BenefitsAccordion :items="benefits" />
        <VImg
          src="/media/images/MP_Athletes.png"
          class="benefits-img"
          :width="420"
          :height="520"
          fit="fill"
        />
      </div>
    </section>

    <div class="gradient-bar-fade"></div>

    <!-- Testimonials Section -->
    <section id="testimonials" class="section-pad text-center">
      <SectionHeader label="What They Say" title="Athlete Testimonials" centered class="reveal" />
      <div class="testimonials-grid reveal">
        <TestimonialCard
          v-for="t in testimonials"
          :key="t.author"
          :quote="t.quote"
          :author="t.author"
          :role="t.role"
        />
      </div>
    </section>

    <div class="gradient-bar-fade"></div>

    <!-- Contact Section -->
    <!-- <section id="contact" class="section-pad text-center">
      <div class="contact-inner reveal">
        <SectionHeader label="Get In Touch" title="Ready to Level Up?" centered />
        <p class="contact-subtitle reveal">
          Have questions about testing, team evaluations, or combine events? We're here to help you
          find the right fit for your goals.
        </p>
        <BaseButton class="reveal" @click="showContact = true">Contact Us</BaseButton>
      </div>
    </section>

    <div class="gradient-bar-fade"></div> -->

    <!-- Partners Section -->
    <section id="partners" class="partners-section">
      <p class="partners-label">Trusted Partners</p>
      <div class="partners-grid">
        <a
          href="https://valdperformance.com"
          target="_blank"
          rel="noopener noreferrer"
          class="partner-link"
          title="VALD Performance"
        >
          <VImg src="/media/images/extracted/image-13.svg" alt="VALD Performance" :height="80" />
        </a>
      </div>
    </section>
  </main>

  <!-- Footer -->
  <SiteFooterCustom />

  <!-- Modals -->
  <EventDetailModal
    v-model="showEventDetail"
    :detail-type="selectedEventDetailType"
    :status="selectedEventStatus"
    @register="openRegistrationFromEventDetail"
    @contact="openContactFromEventDetail"
  />
  <LearnMoreModal
    v-model="showLearnMore"
    :service="selectedService"
    @register="openRegistrationFromLearnMore"
    @contact="openContactFromLearnMore"
  />
  <ContactFormModal v-model="showContact" :preselected-interest="contactInterest" />
  <RegistrationModal
    v-model="showRegistration"
    :events="events"
    :selected-event="selectedEventSlug"
  />
</template>

<style scoped>
/* ─── Scroll Reveal ─── */
:global(.reveal) {
  opacity: 0;
  transform: translateY(2rem);
  transition:
    opacity 0.7s ease,
    transform 0.7s ease;
}

:global(.reveal.visible) {
  opacity: 1;
  transform: none;
}

/* ─── Hero ─── */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 6rem 0;
}

.hero-bg {
  position: absolute;
  inset: 0;
  background-image: url('/media/images/mpower6-elite-banner-image-cropped.jpg');
  background-position-x: 65%;
  background-position-y: center;
  background-size: cover;
  background-repeat: no-repeat;

  @media (min-width: 768px) {
    background-position-x: 90%;
  }
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--darker), transparent 70%) 0%,
    color-mix(in srgb, var(--darker), transparent 15%) 100%
  );
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 820px;
  padding: 0 1.5rem;
}

.hero-eyebrow {
  font-family: 'HND', sans-serif;
  font-size: 0.8rem;
  color: var(--pink-bright);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.hero-headline {
  font-family: 'HND', sans-serif;
  font-weight: 950;
  font-size: clamp(2.5rem, 6vw, 5.2rem);
  color: var(--white);
  text-transform: uppercase;
  line-height: 0.96;
  margin-bottom: 0.5rem;
}

.hero-headline span {
  background: linear-gradient(135deg, var(--pink), var(--purple));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-sub {
  font-family: 'HND', sans-serif;
  font-weight: 300;
  font-size: 1.1rem;
  color: #fff;
  line-height: 1.7;
  max-width: 500px;
  margin: 0 auto 2.25rem;
}

.hero-btns {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.hero-btns button {
  min-width: 13.5rem;
}

/* ─── Section utilities ─── */
.section-pad {
  padding: 6.25rem 3rem;
}

.text-center {
  text-align: center;
}

/* ─── Video Section ─── */
.video-section {
  background: var(--dark);
}

.video-body {
  font-family: 'HND', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
  max-width: 560px;
  margin: 1rem auto 0;
  line-height: 1.7;
}

.video-wrap {
  position: relative;
  max-width: 960px;
  margin: 2.5rem auto 0;
  border-radius: 1rem;
  overflow: hidden;
  cursor: pointer;
  border: 0.0625rem solid rgba(255, 255, 255, 0.06);
}

.video-wrap img {
  width: 100%;
  display: block;
  filter: brightness(0.8);
}

.iframe-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 */
}

.iframe-wrapper iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  background: rgba(208, 23, 122, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.25s,
    box-shadow 0.25s;
}

.play-btn:hover {
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 0 2.5rem rgba(208, 23, 122, 0.5);
}

.play-btn::after {
  content: '';
  display: block;
  width: 0;
  height: 0;
  border-left: 1.375rem solid #fff;
  border-top: 0.875rem solid transparent;
  border-bottom: 0.875rem solid transparent;
  margin-left: 0.25rem;
}

.gradient-bar-fade {
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--blue-purple) 15%,
    var(--pink) 50%,
    var(--purple) 85%,
    transparent
  );
  opacity: 0.5;
}

/* ─── Events Grid ─── */
.events-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 1.75rem;
  margin-top: 3rem;
}

/* ─── Services ─── */
.services-section {
  background: var(--dark);
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.75rem;
  margin-top: 3rem;
}

/* ─── CTA Section ─── */
.cta-section {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, var(--darker) 0%, #0a0f1f 100%);
}

.cta-inner {
  display: flex;
  gap: 3.75rem;
  max-width: 1100px;
  margin: 0 auto;
  align-items: center;
}

.cta-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.cta-text .cta-btns {
  width: 100%;
  display: flex;
}

.cta-text .section-label {
  font-size: 0.75rem;
  color: var(--pink-bright);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
  font-family: 'HND', sans-serif;
  font-weight: 600;
}

.cta-text h2 {
  font-family: 'HND', sans-serif;
  font-weight: 950;
  font-size: clamp(2rem, 3.8vw, 3rem);
  color: var(--white);
  text-transform: uppercase;
  line-height: 1.08;
  margin-bottom: 1rem;
}

.cta-text h2 span {
  background: linear-gradient(135deg, var(--pink-bright), var(--purple));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cta-text p {
  font-family: 'HNT', sans-serif;
  font-size: 1.05rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  max-width: 480px;
}

.cta-cutout {
  flex: 0 0 23.75rem;
}

/* ─── Benefits ─── */
.benefits-section {
  background: var(--dark);
}

.benefits-layout {
  display: flex;
  gap: 3.75rem;
  align-items: center;
  margin-top: 3rem;

  .benefits-accordion {
    flex: 1;
  }
}

.benefits-img {
  padding: 0.5rem;
  flex: 0 0 26.25rem;
  text-align: center;
}

/* ─── Testimonials ─── */
.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.75rem;
  margin-top: 3rem;
}

/* ─── Contact ─── */
.contact-inner {
  max-width: 600px;
  margin: 0 auto;
}

.contact-subtitle {
  font-family: 'HND', sans-serif;
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--gray-light);
  margin: 1.5rem auto 2rem;
}

/* ─── Partners ─── */
.partners-section {
  padding: 32px 24px 20px;
  text-align: center;
}

.partners-label {
  font-family: 'HND', sans-serif;
  font-size: 0.8rem;
  color: var(--pink-bright);
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 60px;
}

.partners-grid {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 40px;
  max-width: 900px;
  margin: 0 auto;
}

.partner-link {
  display: inline-block;
  opacity: 0.85;
  transition:
    opacity 0.3s,
    transform 0.3s;
}

.partner-link:hover {
  opacity: 1;
  transform: scale(1.05);
}

/* ─── Responsive ─── */
/* Desktop */
@media (max-width: 1024px) {
  .section-pad {
    padding: 5rem 2rem;
  }

  .cta-inner {
    gap: 2.5rem;
  }

  .cta-cutout {
    flex: 0 0 18.75rem;
  }

  .benefits-layout {
    gap: 2.5rem;
  }

  .benefits-img {
    flex: 0 0 20rem;
  }
}

/* Tablet */
@media (max-width: 768px) {
  .section-pad {
    padding: 3.5rem 1.125rem;
  }

  .hero-content {
    padding: 0 1.125rem;
  }

  .hero-eyebrow {
    font-size: 0.68rem;
    letter-spacing: 0.12em;
    margin-bottom: 1.125rem;
  }

  .hero-headline {
    font-size: clamp(2rem, 8vw, 3rem);
    line-height: 1;
    margin-bottom: 1rem;
  }

  .hero-sub {
    font-size: 0.92rem;
    margin-bottom: 1.75rem;
  }

  .hero-btns {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }

  /* Video */
  .video-wrap {
    margin: 1.5rem auto 0;
    border-radius: 0.75rem;
  }

  .video-body {
    font-size: 0.9rem;
    margin: 0.75rem auto 0;
  }

  .play-btn {
    width: 3.5rem;
    height: 3.5rem;
  }

  .play-btn::after {
    border-top: 0.625rem solid transparent;
    border-bottom: 0.625rem solid transparent;
    border-left: 1rem solid #fff;
    margin-left: 0.1875rem;
  }

  /* Grids */
  .events-grid,
  .services-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
    margin-top: 2rem;
  }

  .testimonials-grid {
    grid-template-columns: 1fr;
    gap: 1.125rem;
    margin-top: 2rem;
  }

  /* CTA */
  .cta-inner {
    flex-direction: column;
    text-align: center;
    gap: 2rem;
  }

  .cta-text {
    align-items: center;
  }

  .cta-text .cta-btns {
    justify-content: center;
  }

  .cta-text h2 {
    font-size: clamp(1.6rem, 6vw, 2.2rem);
  }

  .cta-text p {
    font-size: 0.95rem;
    margin: 0 auto 1.5rem;
  }

  .cta-cutout {
    flex: 0 0 auto;
    max-width: 240px;
    margin: 0 auto;
  }

  /* Benefits */
  .benefits-layout {
    flex-direction: column;
    gap: 2rem;
    margin-top: 2rem;
  }

  .benefits-img {
    flex: 0 0 auto;
    max-width: 260px;
    margin: 0 auto;
    order: -1;
  }

  .partners-label {
    margin-bottom: 40px;
  }
}

/* ─── Small phones ─── */
@media (max-width: 390px) {
  .section-pad {
    padding: 3rem 0.875rem;
  }

  .hero-headline {
    font-size: clamp(1.7rem, 9vw, 2.4rem);
  }

  .benefits-img {
    max-width: 220px;
  }

  .cta-cutout {
    max-width: 200px;
  }
}

/* ─── Page Background SVG ─── */
.page-bg-svg {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  opacity: 0.045;
}
</style>
